import { useMemo } from 'react';
import { useGetAlarmsQuery } from '../../gql/generated';
import { MobileSearchableSelect } from '../MobileSearchableSelect';
import {
  InlineSearchableList,
  SearchableSelect,
  SearchableSelectOption,
} from '../SearchableSelect';

interface AlarmSignalFilterProps {
  selectedValues: string[];
  onChange: (values: string[]) => void;
  onClose?: () => void;
  variant?: 'inline' | 'popover' | 'mobile';
}

export function AlarmSignalFilter({
  selectedValues,
  onChange,
  onClose,
  variant = 'inline',
}: AlarmSignalFilterProps) {
  // Fetch all alarms to extract unique signals
  const { data, loading } = useGetAlarmsQuery({
    variables: {
      pagination: {
        limit: 10000, // Get all alarms for filter
        offset: 0,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  // Extract unique signals from alarms
  const options: SearchableSelectOption[] = useMemo(() => {
    if (!data?.alarms?.rows) return [];

    const uniqueSignals = Array.from(
      new Set(data.alarms.rows.map((alarm) => alarm.signal))
    ).sort();

    return uniqueSignals.map((signal) => ({
      value: signal,
      label: signal,
    }));
  }, [data]);

  const handleClear = () => {
    onChange([]);
  };

  if (variant === 'mobile') {
    return (
      <MobileSearchableSelect
        options={options}
        selectedValues={selectedValues}
        onSelectionChange={onChange}
        placeholder={loading ? 'Loading signals...' : 'Select signals'}
        label='Signal'
      />
    );
  }

  if (variant === 'popover') {
    return (
      <SearchableSelect
        options={options}
        selectedValues={selectedValues}
        onSelectionChange={onChange}
        placeholder={loading ? 'Loading signals...' : 'Select signals'}
      />
    );
  }

  return (
    <InlineSearchableList
      options={options}
      selectedValues={selectedValues}
      onSelectionChange={onChange}
      placeholder={loading ? 'Loading...' : 'Search signals...'}
      onClearFilter={handleClear}
      onClose={onClose}
    />
  );
}
